import { bridgeFailure, openNotebookBridge } from './execute-live-notebook'

/** Matches `READ_OPEN_CELL_CHARS` / `READ_OPEN_TOTAL_CHARS` in the AI service's `readOpenNotebook.ts`. */
export const CELL_CHARS = 4_000
export const TOTAL_CHARS = 40_000

interface SnapshotCell {
  index: number
  cell_type: string
  source: string
  execution_count: number | null
  output_summary: string
}

interface Snapshot {
  cells?: SnapshotCell[]
  active_index?: number
  kernel_status?: string
  dirty?: boolean
  cell_count?: number
}

type OutCell = (SnapshotCell & { source_truncated?: true }) | { index: number; truncated: true }

/**
 * Each source is cut to `CELL_CHARS`; cells are kept in order while the kept sources fit in
 * `TOTAL_CHARS`, and every cell from the first that does not fit is only `{index, truncated: true}`.
 */
export function capSnapshotCells(cells: readonly SnapshotCell[]): { cells: OutCell[]; truncated: boolean } {
  let total = 0
  let full = false
  let truncated = false

  const out = cells.map((cell): OutCell => {
    const source = typeof cell.source === 'string' ? cell.source : ''
    const cut = source.length > CELL_CHARS ? source.slice(0, CELL_CHARS) : source

    if (full || total + cut.length > TOTAL_CHARS) {
      full = true
      truncated = true

      return { index: cell.index, truncated: true }
    }

    total += cut.length
    if (cut.length === source.length) return { ...cell, source }

    truncated = true

    return { ...cell, source: cut, source_truncated: true }
  })

  return { cells: out, truncated }
}

/** `read_open_notebook`: the open notebook's cells as they are now, through the bridge's `snapshot`. No card. */
export async function executeReadOpenNotebook(input: unknown): Promise<unknown> {
  const open = openNotebookBridge(input)
  if ('success' in open) return open
  const { notebookId, bridge } = open

  try {
    const snap = await bridge.request<Snapshot>('snapshot')
    const { cells, truncated } = capSnapshotCells(Array.isArray(snap?.cells) ? snap.cells : [])

    return {
      success: true,
      notebook_id: notebookId,
      cells,
      active_index: snap?.active_index ?? null,
      kernel_status: snap?.kernel_status ?? null,
      dirty: snap?.dirty ?? null,
      cell_count: snap?.cell_count ?? cells.length,
      ...(truncated ? { truncated: true } : {}),
    }
  } catch (err) {
    return bridgeFailure(notebookId, err)
  }
}
